import {
    AfterViewInit,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    InputSignal,
    output,
    signal,
    Signal,
    viewChild,
    WritableSignal,
} from '@angular/core';
import {GenLegendStyle} from 'generated/serverModels/GenLegendStyle';
import {toSignal} from '@angular/core/rxjs-interop';

import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {MatTooltipModule} from '@angular/material/tooltip';
import {ApplicationConfig} from 'src/app/shared/classes/application-config';
import {UserPreference} from 'src/app/shared/classes/user-preferences';
import {LegendMetric} from 'src/app/shared/enums/legend-metric.enum';
import {GlobalConfigService} from '../../services/global-config.service';
import {nomRange, outageValues, percentValues} from '../../functions/nom-layer-style';

@Component({
    selector: 'eaglei-nom-legend',
    templateUrl: 'nom-legend.component.html',
    styleUrls: ['nom-legend.component.scss'],
    standalone: true,
    imports: [FaIconComponent, MatTooltipModule],
})
export class NomLegendComponent implements AfterViewInit {
    private overrideText: Signal<ElementRef<HTMLElement>> = viewChild('overrideText');
    private overrideHash: Signal<ElementRef<HTMLElement>> = viewChild('overrideHash');

    private globalConfigService = inject(GlobalConfigService);

    /**
     * Controls whether the override toggle functionality is enabled.
     * When true, allows toggling the override style visualization.
     * When false, the override toggle functionality is disabled.
     */
    public allowOverrideToggle = input(true);

    // These are mainly used for the edit preference modal, most of the time they should stay undefined
    public legendMetric: InputSignal<number> = input();
    public legendStyle: InputSignal<GenLegendStyle> = input();

    public toggleOverrideVisibility = output<void>();

    protected preferences: Signal<UserPreference | undefined> = toSignal(ApplicationConfig.currentUserPreferences);

    protected metric = computed(() => {
        if (this.legendMetric()) {
            return this.legendMetric();
        }

        return this.globalConfigService.selectedLegendMetric();
    });

    protected colors = computed(() => {
        if (this.preferences() && this.legendStyle()) {
            this.preferences().legendStyle = this.legendStyle();
        } else if (this.legendStyle()) {
            this.updateOverrideColor(this.legendStyle().overrideColor);
            return this.legendStyle().colors;
        }

        return this.preferences()?.hasLegendStyle() ? this.preferences()?.legendStyle.colors : nomRange;
    });

    protected legendElements = computed(() => {
        const value = [];
        let breakPoints = [];
        if (this.metric() === LegendMetric.CUSTOMERS_OUT) {
            breakPoints = outageValues.map((v) => v.display);
        } else if (this.metric() === LegendMetric.PERCENTAGE) {
            breakPoints = percentValues.map((v) => v.display);
        }

        for (let i = 0; i < this.colors().length; i++) {
            value.push({
                display: breakPoints[i],
                color: this.colors()[i],
            });
        }

        return value;
    });

    protected renderOverrideStyle: WritableSignal<boolean> = signal(ApplicationConfig.showOverrideColor.value);

    constructor(public ele: ElementRef) {}

    ngAfterViewInit(): void {
        this.updateOverrideColor();
    }

    private updateOverrideColor(overrideColor?: string): void {
        if (!overrideColor) {
            if (this.preferences()?.hasLegendStyle()) {
                overrideColor = this.preferences().legendStyle.overrideColor;
            } else {
                overrideColor = ApplicationConfig.overrideColor;
            }
        }

        const backgroundImage = `linear-gradient(45deg, ${overrideColor} 25%, transparent 25%, transparent 50%, ${overrideColor} 50%, ${overrideColor} 75%, transparent 75%, #fff)`;

        this.overrideText().nativeElement.style.color = overrideColor;
        this.overrideHash().nativeElement.style.backgroundImage = backgroundImage;
        this.overrideHash().nativeElement.style.borderColor = overrideColor;
    }

    public toggleShowOverrides(): void {
        // This is needed right now to render overrides... We may not need it soon
        this.renderOverrideStyle.update((currentValue) => {
            ApplicationConfig.showOverrideColor.next(!currentValue);
            return !currentValue;
        });

        this.toggleOverrideVisibility.emit();
    }
}
